import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, computed, inject, model, OnInit, signal } from "@angular/core";
import { NavigationEnd, Router, RouterLink } from "@angular/router";

import { filter } from "rxjs/operators";

import { AuthService } from "@core/services/auth.service";
import { UserPreferencesService } from "@core/services/user-preferences.service";
import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { BaseLoginComponent } from "@login/components/base/base-login.component";
import { RegisterLocalDto } from "@login/models/register.model";
import { LoginRoutesConstants } from "@login/routes/login.routes.constants";
import { AuthHandlerService } from "@login/services/auth-handler.service";
import { ButtonWithLoadingComponent } from "@shared/components/button-with-loading/button-with-loading.component";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { InputComponent } from "@shared/components/input/input.component";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { ModalService } from "@shared/components/modal/modal.service";
import { OrganizationType } from "@shared/models/organization.model";
import { AuthProvider } from "@shared/models/types.model";
import { EMAIL_PATTERN, PASSWORD_PATTERN } from "@shared/validators/custom-validators";

type AuthMode = "login" | "signup";
type AuthStep = "email" | "password";

@Component({
  selector: "component-auth-modal",
  templateUrl: "./auth-modal.component.html",
  imports: [ModalComponent, ButtonWithLoadingComponent, CustomIconComponent, BaseLoginComponent, InputComponent, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthModalComponent extends BaseModalComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _authHandler = inject(AuthHandlerService);
  private readonly _userPreferences = inject(UserPreferencesService);
  private readonly _modalService = inject(ModalService);

  // Inputs for mode and customization
  readonly mode = model<AuthMode>("signup"); // 'login' or 'signup'
  readonly isUpgradeFlow = model<boolean>(false); // Only for signup mode - if upgrading from guest
  readonly isExpired = model<boolean>(false); // Only for signup mode - if session expired (non-closeable)
  readonly customHeaderTitle = model<string | undefined>(undefined);
  readonly customHeaderSubtitle = model<string | undefined>(undefined);

  // Step management
  protected readonly currentStep = signal<AuthStep>("email");
  protected readonly isEmailStep = computed(() => this.currentStep() === "email");
  protected readonly isPasswordStep = computed(() => this.currentStep() === "password");

  // Form state
  protected readonly email = model<string>("");
  protected readonly password = signal("");
  protected readonly emailError = signal<string | null>(null);
  protected readonly passwordError = signal<string | null>(null);
  protected readonly showEmailWarning = signal(false); // For "email exists" or "email not found"
  protected readonly isLoading = signal(false);
  protected readonly currentProvider = signal<AuthProvider | null>(null);
  protected readonly passwordTouched = signal(false); // Only for signup

  // Routes
  protected readonly loginRoutes = LoginRoutesConstants;

  // Computed properties based on mode
  protected readonly isLoginMode = computed(() => this.mode() === "login");
  protected readonly isSignupMode = computed(() => this.mode() === "signup");

  protected readonly headerTitle = computed(() => {
    if (this.customHeaderTitle()) {
      return this.customHeaderTitle();
    }
    if (this.isLoginMode()) {
      return "Welcome back";
    }
    if (this.isExpired()) {
      return "Your guest session has expired";
    }
    return "Create your free account";
  });

  protected readonly headerSubtitle = computed(() => {
    if (this.customHeaderSubtitle()) {
      return this.customHeaderSubtitle();
    }
    if (this.isExpired()) {
      return "To continue working and keep your documents, please create a free account.";
    }
    return undefined;
  });

  protected readonly footerText = computed(() => (this.isLoginMode() ? "Don't have an account?" : "Already have an account?"));

  protected readonly footerLinkText = computed(() => (this.isLoginMode() ? "Sign up for free" : "Log in"));

  protected readonly footerLinkRoute = computed(() =>
    this.isLoginMode() ? this.loginRoutes.signup() : this.loginRoutes.login()
  );

  protected readonly passwordButtonText = computed(() => {
    if (this.isLoginMode()) {
      return "Sign in";
    }
    return this.isUpgradeFlow() ? "Create account" : "Continue";
  });

  protected readonly showCloseButton = computed(() => {
    // hidden if not in upgrade flow
    if (!this.isUpgradeFlow()) {
      return false;
    }

    return !this.isExpired();
  });

  ngOnInit(): void {
    // hide this modal on navigation
    this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.activeModal.close();
    });

    // Restore email from session storage if available (for regular page navigation)
    const storedEmail = sessionStorage.getItem("auth_modal_email");
    if (storedEmail && !this.email()) {
      this.email.set(storedEmail);
      // Clear the stored email after using it
      sessionStorage.removeItem("auth_modal_email");
    }
  }

  protected onEmailChange(value: string): void {
    this.email.set(value);
    this.showEmailWarning.set(false);
    if (this.isSignupMode()) {
      this.emailError.set(null);
    }
  }

  protected onEmailContinue(): void {
    if (!this.email() || !EMAIL_PATTERN.test(this.email())) {
      this.emailError.set("Please enter a valid email address");
      this.showEmailWarning.set(false);
      return;
    }

    this.emailError.set(null);
    this.showEmailWarning.set(false);
    this.isLoading.set(true);

    this._authService.checkEmail(this.email()).subscribe({
      next: (response) => {
        this.isLoading.set(false);

        if (this.isLoginMode()) {
          // Login: email should exist
          if (response.exists) {
            this.currentStep.set("password");
          } else {
            this.showEmailWarning.set(true);
          }
        } else {
          // Signup: email should NOT exist
          if (response.exists) {
            this.showEmailWarning.set(true);
          } else {
            this.currentStep.set("password");
          }
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.emailError.set("Failed to verify email. Please try again.");
      }
    });
  }

  protected onPasswordChange(value: string): void {
    this.password.set(value);

    // Only validate dynamically for signup after first touch
    if (this.isSignupMode() && this.passwordTouched()) {
      this.validatePasswordForSignup();
    }
  }

  private validatePasswordForSignup(): string | null {
    if (!this.password()) {
      const error = "Password is required";
      this.passwordError.set(error);
      return error;
    }

    if (this.password().length < 8) {
      const error = "Password must be at least 8 characters";
      this.passwordError.set(error);
      return error;
    }

    if (!PASSWORD_PATTERN.test(this.password())) {
      const error =
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character";
      this.passwordError.set(error);
      return error;
    }

    this.passwordError.set(null);
    return null;
  }

  protected onPasswordAction(): void {
    if (this.isLoginMode()) {
      this.handleLogin();
    } else {
      this.handleSignup();
    }
  }

  private handleLogin(): void {
    if (!this.password()) {
      this.passwordError.set("Please enter your password");
      return;
    }

    this.passwordError.set(null);
    this.isLoading.set(true);

    this._authService.loginWithCredentials(this.email(), this.password()).subscribe({
      next: () => {
        this._router.navigate([DocumentationRoutesConstants.main()]);
      },
      error: (err: HttpErrorResponse) => {
        const code = (err.error as any)?.code;
        if (code === "INVALID_CREDENTIALS") {
          this.passwordError.set("Invalid email or password");
        } else {
          this.passwordError.set((err.error as any)?.message || "Sign in failed");
        }
        this.isLoading.set(false);
      }
    });
  }

  private handleSignup(): void {
    this.passwordTouched.set(true);

    const error = this.validatePasswordForSignup();
    if (error) {
      return;
    }

    this.isLoading.set(true);

    // Check if this is upgrade flow or regular signup
    if (this.isUpgradeFlow()) {
      this.handleUpgrade();
    } else {
      this.handleRegularSignup();
    }
  }

  private handleUpgrade(): void {
    this._authService.upgradeFromGuest({ email: this.email(), password: this.password() }).subscribe({
      next: () => {
        this._authService.updateCurrentUser();
        this.activeModal.close(true);
        // Show billing modal after upgrade
        setTimeout(() => this._modalService.openBillingModal(this._authService.currentUser()?.hasPlan ?? false), 300);
      },
      error: (err: HttpErrorResponse) => {
        const code = (err.error as any)?.code;
        if (code === "EMAIL_ALREADY_EXISTS") {
          this.emailError.set("This email is already in use");
          this.currentStep.set("email");
          this.showEmailWarning.set(true);
        } else {
          this.passwordError.set((err.error as any)?.message || "Upgrade failed");
        }
        this.isLoading.set(false);
      }
    });
  }

  private handleRegularSignup(): void {
    const signupData: RegisterLocalDto = {
      email: this.email(),
      password: this.password(),
      organizationType: OrganizationType.INDIVIDUAL
    };

    const orgId = this._authHandler.organizationId();
    if (orgId) {
      signupData.organizationId = orgId;
    }

    this._authService.registerLocal(signupData).subscribe({
      next: () => {
        this._userPreferences.setIsNewUser(true);
        this.activeModal.close(true);
        this._router.navigate([DocumentationRoutesConstants.main()]);
      },
      error: (err: HttpErrorResponse) => {
        const code = (err.error as any)?.code;
        if (code === "EMAIL_ALREADY_EXISTS") {
          this.emailError.set("This email is already in use");
          this.currentStep.set("email");
          this.showEmailWarning.set(true);
        } else {
          this.passwordError.set((err.error as any)?.message || "Sign up failed");
        }
        this.isLoading.set(false);
      }
    });
  }

  protected onBackToEmail(): void {
    this.currentStep.set("email");
    this.password.set("");
    this.passwordError.set(null);
    this.passwordTouched.set(false);
    this.showEmailWarning.set(false);
  }

  protected onOAuthAction(provider: AuthProvider): void {
    this.isLoading.set(true);
    this.currentProvider.set(provider);

    if (this.isSignupMode() && this.isUpgradeFlow()) {
      // OAuth for upgrade
      this._authService.loginWithProviderForUpgrade(provider).subscribe({
        next: (authUrl) => {
          window.location.href = authUrl;
        },
        error: (err) => {
          this.emailError.set((err as any)?.error?.message || "Provider login failed");
          this.isLoading.set(false);
          this.currentProvider.set(null);
        }
      });
    } else {
      // Regular OAuth (login or signup)
      this._authHandler.authenticateWithProvider(provider);
    }
  }

  protected onFooterLinkClick(event?: Event): void {
    // Store current email to preserve it in both cases
    const currentEmail = this.email();

    // If this is an upgrade flow (temp user), switch modes without navigation
    if (this.isUpgradeFlow()) {
      event?.preventDefault();

      // Open opposite mode with preserved email
      if (this.isLoginMode()) {
        // Switch from login to signup
        const modalRef = this._modalService.openSignupModal(this.isUpgradeFlow(), this.isExpired());
        if (modalRef.componentInstance && currentEmail) {
          modalRef.componentInstance.email.set(currentEmail);
        }
      } else {
        // Switch from signup to login
        const modalRef = this._modalService.openLoginModal(this.isUpgradeFlow(), this.isExpired());
        if (modalRef.componentInstance && currentEmail) {
          modalRef.componentInstance.email.set(currentEmail);
        }
      }
    } else {
      if (currentEmail) {
        // For regular navigation, store email in session storage to preserve across page navigation
        sessionStorage.setItem("auth_modal_email", currentEmail);
      }

      this._router.navigate([this.footerLinkRoute()]);
    }
  }

  protected onForgotPassword(): void {
    // Open password reset modal with prefilled email
    const modalRef = this._modalService.openPasswordResetModal();
    if (modalRef.componentInstance && this.email()) {
      modalRef.componentInstance.email.set(this.email());
    }
  }
}
